import { useEffect, useMemo, useState } from "react";
import type { GetStaticPaths, GetStaticProps } from "next";
import Link from "next/link";
import parse, { domToReact, type DOMNode, type Element } from "html-react-parser";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import nextI18NextConfig from "../../next-i18next.config";
import Layout from "../../components/layout";
import Accordion from "../../components/v2/accordion/accordion";
import { CopyableCode, extractText } from "../../components/v2/copyable-code";
import {
    articleSections,
    findTopic,
    knowledgeArticles,
    splitFaqSection,
    type KnowledgeArticle,
} from "../../lib/knowledge-articles";
import "../../styles/scss/knowledge.module.scss";

type Props = {
    article: KnowledgeArticle;
    related: Pick<KnowledgeArticle, "slug" | "title" | "topic">[];
    pageMeta: {
        pageTitle: string;
        ogImage: string;
        uri: string;
        metaTitle: string;
        metaDesc: string;
        metaRobotsNoindex: string;
        metaRobotsNofollow: string;
    };
};

/**
 * Gives every <pre> in the body the blog's copy button. Parsed rather than
 * authored into the content so the article HTML stays plain.
 */
const codeBlockOptions = {
    replace: (node: Element) => {
        if (node.name !== "pre") return undefined;
        const text = extractText(node.children as DOMNode[]);
        return (
            <CopyableCode text={text}>
                <pre>{domToReact(node.children as DOMNode[])}</pre>
            </CopyableCode>
        );
    },
};

const KnowledgeArticlePage = ({ article, related, pageMeta }: Props) => {
    const sections = useMemo(() => articleSections(article.body), [article.body]);
    /* FAQ questions render through the shared Accordion, as on the blog, rather
       than as a flat run of headings. Sections are still read from the full
       body, so the sidebar is unaffected. */
    const { before, faqHeading, faqs, after } = useMemo(
        () => splitFaqSection(article.body),
        [article.body]
    );
    const [activeSection, setActiveSection] = useState(sections[0]?.id ?? "");
    const topic = findTopic(article.topic);

    /**
     * Highlights the section currently being read. rootMargin pulls the trigger
     * line near the top of the viewport so a heading counts as "active" once it
     * reaches the top rather than when it first appears at the bottom.
     */
    useEffect(() => {
        if (sections.length === 0 || typeof IntersectionObserver === "undefined") return;

        const observer = new IntersectionObserver(
            (entries) => {
                const visible = entries
                    .filter((entry) => entry.isIntersecting)
                    .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
                if (visible?.target.id) setActiveSection(visible.target.id);
            },
            { rootMargin: "-120px 0px -70% 0px", threshold: 0 }
        );

        sections.forEach(({ id }) => {
            const element = document.getElementById(id);
            if (element) observer.observe(element);
        });

        return () => observer.disconnect();
    }, [sections]);

    return (
        <Layout
            pageTitle={pageMeta.pageTitle}
            ogImage={pageMeta.ogImage}
            uri={pageMeta.uri}
            metaTitle={pageMeta.metaTitle}
            metaDesc={pageMeta.metaDesc}
            metaRobotsNoindex={pageMeta.metaRobotsNoindex}
            metaRobotsNofollow={pageMeta.metaRobotsNofollow}
        >
            <div className="knowledge-page knowledge-article-page">
                <section className="knowledge-article-hero">
                    <div className="container">
                        <Link href="/knowledge/" className="knowledge-back">
                            <span className="knowledge-back-icon" aria-hidden="true">
                                <span className="knowledge-chevron" />
                            </span>
                            Back to category
                        </Link>

                        {topic && <div className="knowledge-article-badge">{topic.label}</div>}
                        <h1>{article.title}</h1>
                        <p className="knowledge-article-intro">{article.description}</p>
                    </div>
                </section>

                <div className="container knowledge-article-layout">
                    <aside className="knowledge-toc" aria-label="What's in this article">
                        <div className="knowledge-toc-inner">
                            <p className="knowledge-toc-title">What&apos;s in this article</p>
                            <nav className="knowledge-toc-list">
                                {sections.map((section) => (
                                    <a
                                        key={section.id}
                                        href={`#${section.id}`}
                                        className={activeSection === section.id ? "is-active" : ""}
                                        onClick={() => setActiveSection(section.id)}
                                    >
                                        {section.label}
                                    </a>
                                ))}
                            </nav>
                        </div>
                    </aside>

                    <article className="knowledge-article-body">
                        {parse(before, codeBlockOptions)}

                        {faqHeading && faqs.length > 0 && (
                            <div className="knowledge-faq">
                                <h2 id={faqHeading.id}>{faqHeading.label}</h2>
                                <Accordion
                                    defaultActiveId={faqs[0].id}
                                    faqs={faqs.map((faq) => ({
                                        id: faq.id,
                                        header: faq.header,
                                        text: <>{parse(faq.answerHtml, codeBlockOptions)}</>,
                                        // Plain text drives the FAQ JSON-LD the
                                        // Accordion emits for this page.
                                        answerPlain: faq.answerHtml
                                            .replace(/<[^>]*>/g, " ")
                                            .replace(/\s+/g, " ")
                                            .trim(),
                                    }))}
                                />
                            </div>
                        )}

                        {after && parse(after, codeBlockOptions)}
                    </article>
                </div>

                {related.length > 0 && (
                    <section className="knowledge-related">
                        <div className="knowledge-article-container">
                            <h2>Related Content</h2>
                            <div className="knowledge-related-cards">
                                {related.map((item) => {
                                    const itemTopic = findTopic(item.topic);
                                    return (
                                        <Link
                                            href={`/knowledge/${item.slug}/`}
                                            className="knowledge-related-card"
                                            key={item.slug}
                                        >
                                            {itemTopic && (
                                                <span className="knowledge-related-badge">
                                                    {itemTopic.label}
                                                </span>
                                            )}
                                            <p>{item.title}</p>
                                        </Link>
                                    );
                                })}
                            </div>
                        </div>
                    </section>
                )}
            </div>
        </Layout>
    );
};

export const getStaticProps: GetStaticProps<Props> = async ({ params, locale }) => {
    const slug = typeof params?.slug === "string" ? params.slug : "";
    const article = knowledgeArticles.find((item) => item.slug === slug);

    if (!article) {
        return { notFound: true };
    }

    // Same topic first, then anything else, capped at the three the design shows.
    const related = knowledgeArticles
        .filter((item) => item.slug !== article.slug)
        .sort((a, b) => Number(b.topic === article.topic) - Number(a.topic === article.topic))
        .slice(0, 3)
        .map(({ slug: relatedSlug, title, topic }) => ({ slug: relatedSlug, title, topic }));

    const uri = `/knowledge/${article.slug}/`;
    const ogImage =
        "https://middleware.io/wp-content/uploads/2024/08/middleware-observability-thumbnail.png";

    return {
        props: {
            article,
            related,
            pageMeta: {
                pageTitle: article.metaTitle,
                ogImage,
                uri,
                metaTitle: article.metaTitle,
                metaDesc: article.metaDescription,
                metaRobotsNoindex: "index",
                metaRobotsNofollow: "follow",
            },
            pageTitle: article.metaTitle,
            metaTitle: article.metaTitle,
            description: article.metaDescription,
            uri,
            ogImage,
            metaRobotsNoindex: "index",
            metaRobotsNofollow: "follow",
            ...(await serverSideTranslations(
                locale ?? "en",
                // "common" is 586KB and nothing on this page reads it — the text comes
            // from lib/knowledge-articles.ts, and Layout only needs header/footer.
            ["header", "footer"],
                nextI18NextConfig
            )),
        },
    };
};

export const getStaticPaths: GetStaticPaths = async ({ locales }) => {
    const paths = knowledgeArticles.flatMap((article) =>
        locales && locales.length > 0
            ? locales.map((locale) => ({ params: { slug: article.slug }, locale }))
            : [{ params: { slug: article.slug } }]
    );

    return { paths, fallback: false };
};

export default KnowledgeArticlePage;
